/*
 * Copyright (c) 2020. American Express Company
 */

// theirs
import { LocalDate } from '@js-joda/core';
import $ from 'jquery';
import _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';

// models and services
import LiteralsService from '../../services/literals/LiteralsService';
import { FullUserModel } from 'src/types/user.model';

// components
import Drawer from '../../components/drawer/Drawer';
import Goal from 'src/bw/goal/Goal';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// styles
import './GoalsDrawer.scss';

export interface CardInterface {
    name: string;
    key: string;
    types: string[];
    singleton: Boolean;
}

export const goalsList: CardInterface[] = [
    {
        name: 'Take a vacation',
        key: 'takeAVacation',
        types: ['Moment', 'Savings Goal'],
        singleton: false
    },
    {
        name: 'Make a large purchase',
        key: 'makeALargePurchase',
        types: ['Moment', 'Savings Goal'],
        singleton: false
    },
    {
        name: 'Buy new home',
        key: 'buyANewHome',
        types: ['Moment', 'Savings Goal'],
        singleton: false
    },
    {
        name: 'Budget expenses',
        key: 'budgetExpenses',
        types: ['Scenario'],
        singleton: false
    },
    {
        name: 'Pay down debt',
        key: 'payDownDebt',
        types: ['Scenario'],
        singleton: true
    },
    {
        name: 'Save Emergency fund',
        key: 'saveEmergencyFund',
        types: ['Savings Goal'],
        singleton: true
    },
    {
        name: 'New job',
        key: 'newJob',
        types: ['Moment'],
        singleton: false
    },
    {
        name: 'Get promoted',
        key: 'getPromoted',
        types: ['Moment'],
        singleton: false
    },
    {
        name: 'Quit job',
        key: 'quitJob',
        types: ['Moment'],
        singleton: false
    },
    {
        name: 'Retire',
        key: 'retire',
        types: ['Moment'],
        singleton: false
    },
    {
        name: 'Lose job',
        key: 'loseJob',
        types: ['Moment'],
        singleton: false
    },
    {
        name: 'Have a baby',
        key: 'haveABaby',
        types: ['Scenario'],
        singleton: false
    },
    {
        name: 'Donate a portion of your income',
        key: 'donate',
        types: ['Scenario'],
        singleton: false
    },
    {
        name: 'Plan for Education Costs',
        key: 'planForEducation',
        types: ['Scenario'],
        singleton: false
    },
    {
        name: 'Receive inheritance',
        key: 'receiveInheritance',
        types: ['Moment'],
        singleton: false
    },
    {
        name: 'Adjust expenses',
        key: 'reduceExpenses',
        types: ['Scenario'],
        singleton: false
    },
    {
        name: 'Windfall',
        key: 'windfall',
        types: ['Moment'],
        singleton: false
    },
    {
        name: 'Collect Social Security',
        key: 'collectSocialSecurity',
        types: ['Moment'],
        singleton: false
    },
    {
        name: 'Estimate Life Insurance',
        key: 'lifeInsurance',
        types: ['Scenario'],
        singleton: false
    },
    {
        name: 'End of Life',
        key: 'endOfLife',
        types: ['Moment'],
        singleton: true
    },
    {
        name: '2008 Crash',
        key: '2008Crash',
        types: ['Moment'],
        singleton: false
    },
    {
        name: '2010 Boom',
        key: '2010boom',
        types: ['Moment'],
        singleton: false
    },
    {
        name: 'Oil Shock',
        key: 'oilShock',
        types: ['Moment'],
        singleton: false
    },
    {
        name: 'Care for a family member',
        key: 'careForFamilyMember',
        types: ['Moment'],
        singleton: false
    }
];

export const cardsRequiringIncome: string[] = [
    'Get promoted',
    'Quit job',
    'Lose job',
    'Donate a portion of your income'
];

export default class GoalsDrawer extends Component<any> {
    public override componentDidMount(): void {
        if (_.get(this.props, 'mentor.state') === 'addGoal') {
            this.props.triggerGoalDrawer(this.props.mentor.goal);
        }
    }

    public override render(): ReactComponentElement<any> {
        const selectedGoal: string = _.get(this.props, 'mentor.goal');
        const user: FullUserModel = this.props.user;
        const drawerDescription: string = LiteralsService.getLiteral('GoalsDrawer.drawerDescription');
        const filteredGoalsList = _.filter(goalsList, (goalSpec) => goalSpec.key !== 'budgetExpenses');

        return (
            <Drawer
                drawerProps={this.props.drawerProps}
                handleBack={this.props.handleBack}
                withBackButton
                title="Life Events"
            >
                <Typography variant={TypographyVariants.SUBTEXT} className="description">
                    {drawerDescription}
                </Typography>
                <div className="goals-container" key="goals-container">
                    {filteredGoalsList.map((goal: CardInterface, index: number) => {
                        if (!selectedGoal && user.recommendedCards.includes(goal.name)) {
                            return null;
                        }
                        return !this.props.drawerProps.filterType ||
                            goal.types.includes(this.props.drawerProps.filterType)
                            ? this.showGoal(goal, `goal${index}`, selectedGoal === goal.name)
                            : null;
                    })}
                </div>
            </Drawer>
        );
    }

    private filterDrawer(filterType: string): void {
        this.props.setDrawer('goals', { filterType });
    }

    private async handleDoubleClick(event: any): Promise<void> {
        if (event.altKey) {
            const target: JQuery = $(event.target);
            const obj: JQuery = target.is('div.bw-drawer-goal')
                ? target.find('.goal-inner')
                : target.parentsUntil('div.bw-drawer-goal');
            const goalName: string = obj.find('p').text().toLowerCase();
            const startDate: LocalDate = LocalDate.now().plusMonths(2).withDayOfMonth(1);
            await this.props.handleCreateCard({ description: goalName }, startDate);
        }
    }

    private async handleClick(event: any, description: string): Promise<void> {
        const target: JQuery = $(event.target);
        const obj: JQuery = target.is('div.bw-drawer-goal')
            ? target.find('.goal-inner')
            : target.closest('div.bw-drawer-goal');
        const goalName: string = description || obj.find('p').text().toLowerCase();
        let startDate: LocalDate;

        switch (goalName) {
            case 'budget expenses':
                startDate = LocalDate.now().withDayOfMonth(1);
                break;

            case 'collect social security':
                if (this.props.user.dob) {
                    startDate = this.props.user.dob.plusYears(67);
                }
                break;

            case 'end of life':
                if (this.props.user.dob) {
                    startDate = this.props.user.dob.plusYears(90);
                }
                break;

            default:
                startDate = LocalDate.now().plusMonths(2).withDayOfMonth(1);
                break;
        }

        await this.props.handleCreateCard({ description: goalName }, startDate);
    }

    private showGoal(goal: CardInterface, key: string, selected: Boolean): JSX.Element {
        let goalDisabled: boolean = this.props.mentor.onlySelected && !selected;
        let tooltip: string = null;

        if (goal.singleton && _.find(this.props.user.cards, { description: goal.name.toLowerCase() })) {
            selected = false;
            goalDisabled = true;
            tooltip = `You can only have one ${goal.name} goal`;
        } else if (goal.name === 'Pay down debt' && !this.props.user.debts.length) {
            selected = false;
            goalDisabled = true;
            tooltip = LiteralsService.getLiteral('GoalsDrawer.noDebtTooltipText');
        } else if (_.isEmpty(this.props.drawerProps.incomeSources) && _.includes(cardsRequiringIncome, goal.name)) {
            selected = false;
            goalDisabled = true;
            tooltip = LiteralsService.getLiteral('GoalsDrawer.validIncomeSourceTooltipText');
        }

        return (
            <Goal
                tooltip={tooltip}
                goalData={goal}
                key={key}
                selected={selected}
                disabled={goalDisabled}
                onlySelected={this.props.onlySelected}
                inDrawer={true}
                filterDrawer={this.filterDrawer.bind(this)}
                handleDoubleClick={this.handleDoubleClick.bind(this)}
                handleClick={_.debounce(this.handleClick.bind(this), 500)}
                user={this.props.user}
                context={this.props.context}
            />
        );
    }
}
